import { useState } from 'react'
import { useGreetingName } from '@/hooks/useGreetingName'
import { motion, AnimatePresence } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import { copy } from './Slide3GoogleYou.copy'

const ease = [0.16, 1, 0.3, 1] as const

// Sentinel returned by useGreetingName when no first name is stored. Never
// rendered: the profile card falls back to `c.profileFallbackName` instead.
const GREETING_PLACEHOLDER = 'Customer'

export default function Slide3GoogleYou(_props: SlideProps) {
  const { isActive } = _props
  const c = useSlideCopy(copy)
  const isAr = useLocale() === 'ar'
  // Slideovers and the "Private" overlay enter from the end edge, which is
  // the left under RTL.
  const offscreen = isAr ? '-100%' : '100%'
  const greetingName = useGreetingName()
  const [guideOpen, setGuideOpen] = useState(false)
  const [instagramOpen, setInstagramOpen] = useState(false)
  const profileName = greetingName !== GREETING_PLACEHOLDER ? greetingName : c.profileFallbackName

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 mx-auto max-w-3xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.05, ease }}
        className="mb-6 w-full max-w-[220px]"
      >
        <div className="relative overflow-hidden rounded-[24px] bg-white shadow-[0_14px_36px_rgba(15,23,42,0.08)]">
          <div className="flex items-center gap-3 px-4 py-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-[#0866FF] shadow-sm">
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                <path d="M13.5 21.5v-8.01h2.697l.404-3.132H13.5V8.365c0-.907.252-1.525 1.554-1.525H16.7V4.037a22.2 22.2 0 00-2.424-.124c-2.4 0-4.04 1.465-4.04 4.155v2.318H7.5v3.132h2.736V21.5h3.264z" />
              </svg>
            </div>
            <div className="min-w-0 text-start">
              <p className="text-sm font-semibold text-slate-900">
                {profileName}
              </p>
              <p className="text-xs text-slate-500">
                {c.profileStatus}
              </p>
            </div>
          </div>

          <motion.div
            initial={{ x: offscreen, opacity: 0.94 }}
            animate={isActive ? { x: 0, opacity: 1 } : { x: offscreen, opacity: 0.94 }}
            transition={{ duration: 1.05, delay: 0.4, ease }}
            className="absolute inset-y-0 end-0 start-0 flex items-center justify-center bg-white/60 px-4 backdrop-blur-[10px]"
          >
            <div className="inline-flex items-center gap-1.5">
              <span className="flex h-8 w-8 translate-x-0.5 items-center justify-center">
                <svg className="h-4.5 w-4.5 text-slate-700" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              <p className="text-base font-semibold tracking-tight text-slate-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.9)]">
                {c.privateBadge}
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15, ease }}
        className="text-4xl sm:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.1] text-balance"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.45, ease }}
        className="mt-6 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-2xl"
      >
        {c.body}
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7, ease }}
        className="mt-6 text-base sm:text-lg text-slate-400 italic leading-relaxed max-w-xl"
      >
        {c.ruleOfThumb}
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.95, ease }}
        onClick={() => setGuideOpen(true)}
        className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 cursor-pointer"
      >
        {c.guideButton}
        <svg className="dir-flip h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
      </motion.button>

      {/* Slideover: deep-links to privacy settings */}
      <AnimatePresence>
        {guideOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setGuideOpen(false)}
              className="fixed inset-0 z-40 bg-slate-900/40"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 w-full max-w-md bg-white shadow-2xl overflow-y-auto text-start"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/90 backdrop-blur px-6 py-4">
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-violet-600">
                    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <p className="text-sm font-semibold text-slate-700">
                    {c.guideEyebrow}
                  </p>
                </div>
                <button
                  onClick={() => setGuideOpen(false)}
                  aria-label={c.closeAria}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700 cursor-pointer"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-7 sm:px-8">
                <h3 className="text-2xl sm:text-[1.75rem] font-semibold text-slate-900 tracking-tight leading-tight">
                  {c.guideTitle}
                </h3>

                <h4 className="mt-7 text-base font-semibold text-slate-900">
                  {c.setPrivateHeading}
                </h4>

                <div className="mt-3 space-y-2.5">
                  {/* Facebook */}
                  <a
                    href="https://www.facebook.com/privacy/checkup/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-violet-200 hover:bg-violet-50/30 transition-colors cursor-pointer"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0866FF]">
                      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                        <path d="M13.5 21.5v-8.01h2.697l.404-3.132H13.5V8.365c0-.907.252-1.525 1.554-1.525H16.7V4.037a22.2 22.2 0 00-2.424-.124c-2.4 0-4.04 1.465-4.04 4.155v2.318H7.5v3.132h2.736V21.5h3.264z"/>
                      </svg>
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-semibold text-slate-900">
                        {c.facebookName}
                      </p>
                      <p className="text-sm text-slate-500 leading-snug">
                        {c.facebookDesc}
                      </p>
                    </div>
                    <svg className="dir-flip h-4 w-4 shrink-0 text-slate-300 group-hover:text-violet-500 transition-colors" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </a>

                  {/* Instagram accordion: no reliable deep link, so expand to show steps */}
                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <button
                      onClick={() => setInstagramOpen((v) => !v)}
                      className="group flex w-full items-center gap-3.5 px-4 py-3 hover:bg-violet-50/30 transition-colors cursor-pointer text-start"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#FFD76F] via-[#E1306C] to-[#833AB4]">
                        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                          <path d="M12 2.163c3.204 0 3.584.012 4.849.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.849.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                        </svg>
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-slate-900">
                          {c.instagramName}
                        </p>
                        <p className="text-sm text-slate-500 leading-snug">
                          {c.instagramDesc}
                        </p>
                      </div>
                      <svg
                        className={`dir-flip h-4 w-4 shrink-0 text-slate-300 group-hover:text-violet-500 transition-all ${instagramOpen ? 'rotate-90' : ''}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <AnimatePresence initial={false}>
                      {instagramOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                            <ol className="mt-3 space-y-2.5 text-sm text-slate-600 leading-relaxed list-decimal ps-5 marker:text-slate-400">
                              <li>
                                {c.igStep1Lead}
                                <svg className="mx-1.5 inline-block h-4 w-4 align-[-3px] text-slate-700" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                                  <line x1="3.5" y1="7" x2="16.5" y2="7" />
                                  <line x1="3.5" y1="13" x2="16.5" y2="13" />
                                </svg>
                                <span className="font-medium text-slate-800">{c.igStep1More}</span>
                                {c.igStep1Mid}
                                <svg className="mx-1.5 inline-block h-4 w-4 align-[-3px] text-slate-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="3" />
                                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
                                </svg>
                                <span className="font-medium text-slate-800">{c.igStep1Settings}</span>{c.igStep1Tail}
                              </li>
                              <li>
                                {c.igStep2Lead}<span className="font-medium text-slate-800">{c.igStep2Emph}</span>{c.igStep2Tail}
                              </li>
                              <li>
                                {c.igStep3Lead}
                                <svg className="mx-1.5 inline-block h-4 w-[26px] align-[-4px]" viewBox="0 0 26 16" fill="none">
                                  <rect x="0.75" y="0.75" width="24.5" height="14.5" rx="7.25" fill="#E2E8F0" stroke="#CBD5E1" strokeWidth="0.5" />
                                  <circle cx="8" cy="8" r="5.5" fill="white" stroke="#CBD5E1" strokeWidth="0.5" />
                                </svg>
                                <span className="font-medium text-slate-800">{c.igStep3Toggle}</span>
                                {c.igStep3Mid}<span className="font-medium text-slate-800">{c.igStep3Emph}</span>{c.igStep3Tail}
                              </li>
                              <li>{c.igStep4Lead}<span className="font-medium text-slate-800">{c.igStep4Emph}</span>{c.igStep4Tail}</li>
                            </ol>
                            <a
                              href="https://help.instagram.com/448523408565555"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors cursor-pointer"
                            >
                              {c.igHelpLink}
                              <svg className="dir-flip h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                              </svg>
                            </a>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <h4 className="mt-7 text-base font-semibold text-slate-900">
                  {c.reviewHeading}
                </h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {c.reviewBefore}
                  <a
                    href="https://www.facebook.com/help/288066747875915"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-violet-600 hover:text-violet-700 underline decoration-violet-200 decoration-2 underline-offset-2 transition-colors"
                  >
                    {c.reviewLink}
                  </a>
                  {c.reviewAfter}
                </p>

                <h4 className="mt-6 text-base font-semibold text-slate-900">
                  {c.quizzesHeading}
                </h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {c.quizzesBody}
                </p>

                <h4 className="mt-6 text-base font-semibold text-slate-900">
                  {c.shareHeading}
                </h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {c.shareBody}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
